import { io, Socket } from 'socket.io-client';
import { env } from '@/lib/env';

interface NotificationPayload {
  id: string;
  type: string;
  title: string;
  message: string;
  relatedHoldRequestId?: string;
  relatedLineItemId?: string;
  createdAt: string;
}

interface StatusUpdatePayload {
  entityType: 'hold_request' | 'line_item';
  entityId: string;
  oldStatus: string;
  newStatus: string;
  updatedBy: string;
  updatedAt: Date;
}

type NotificationCallback = (notification: NotificationPayload) => void;
type StatusUpdateCallback = (update: StatusUpdatePayload) => void;
type UnreadCountCallback = (count: number) => void;
type ConnectionCallback = (connected: boolean) => void;

class WebSocketClient {
  private socket: Socket | null = null;
  private token: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  private isManualDisconnect = false;

  // Event listeners
  private notificationListeners: Set<NotificationCallback> = new Set();
  private statusUpdateListeners: Map<string, Set<StatusUpdateCallback>> = new Map();
  private unreadCountListeners: Set<UnreadCountCallback> = new Set();
  private connectionListeners: Set<ConnectionCallback> = new Set();

  /**
   * Connect to WebSocket server
   */
  connect(token: string, serverUrl?: string): void {
    // Same token, socket already exists:
    if (this.socket !== null && this.token === token) {
      if (this.socket.connected) {
        // Already connected — nothing to do.
        return;
      }
      // Socket exists but is disconnected (e.g. after an io server disconnect
      // or after reconnection attempts were exhausted). Reuse the existing
      // Socket.IO client instead of creating a second one — calling io() again
      // while the first socket is still present would leak it.
      this.isManualDisconnect = false;
      this.socket.connect();
      return;
    }

    // Token changed — tear down the old connection first.
    if (this.socket && this.token !== token) {
      this.disconnect();
    }

    this.token = token;
    this.isManualDisconnect = false;

    const url = serverUrl || env.API_URL;

    if (env.isDev) console.log('🔌 Connecting to WebSocket:', url);

    this.socket = io(url, {
      auth: {
        token: this.token,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
      reconnectionDelayMax: this.maxReconnectDelay,
    });

    this.setupEventHandlers();
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    const isDev = env.isDev;

    // Connection events
    this.socket.on('connect', () => {
      if (isDev) console.log('✅ WebSocket connected');
      this.reconnectAttempts = 0;
      this.notifyConnectionListeners(true);
    });

    this.socket.on('connected', (data: { userId?: string; socketId?: string }) => {
      if (isDev) console.log('WebSocket connection confirmed:', data);
    });

    this.socket.on('disconnect', (reason: string) => {
      if (isDev) console.log('❌ WebSocket disconnected:', reason);
      this.notifyConnectionListeners(false);

      // Socket.io's built-in reconnection handles most cases.
      // Only manually reconnect for 'io server disconnect' (server forced disconnect)
      // since Socket.io won't auto-reconnect for that reason.
      if (!this.isManualDisconnect && reason === 'io server disconnect') {
        this.attemptReconnect();
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      if (isDev) console.error('WebSocket connection error:', error.message);
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        if (isDev) console.error('Max reconnection attempts reached, will retry on next user action');
        this.notifyConnectionListeners(false);
      }
    });

    // Notification events
    this.socket.on('notification', (data: { payload: NotificationPayload }) => {
      if (isDev) console.log('📬 Received notification:', data.payload);
      this.notifyNotificationListeners(data.payload);
    });

    // Status update events
    this.socket.on('hold_request:update', (data: { payload: StatusUpdatePayload }) => {
      if (isDev) console.log('🔄 Hold request update:', data.payload);
      this.notifyStatusUpdateListeners(`hold_request:${data.payload.entityId}`, data.payload);
    });

    this.socket.on('line_item:update', (data: { payload: StatusUpdatePayload }) => {
      if (isDev) console.log('🔄 Line item update:', data.payload);
      this.notifyStatusUpdateListeners(`line_item:${data.payload.entityId}`, data.payload);
    });

    // Unread count updates
    this.socket.on('notifications:unread_count', (data: { count: number }) => {
      if (isDev) console.log('📊 Unread count update:', data.count);
      this.notifyUnreadCountListeners(data.count);
    });

    // System messages
    this.socket.on('system:message', (data: { message: string; level: string }) => {
      if (isDev) console.log(`[System ${data.level}]:`, data.message);
    });

    // Ping/pong for connection health
    this.socket.on('pong', (data: { timestamp: string }) => {
      if (isDev) console.log('🏓 Pong received:', data.timestamp);
    });
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  private attemptReconnect(): void {
    if (this.isManualDisconnect || !this.token) return;

    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );

    if (env.isDev) {
      console.log(`Attempting reconnection in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
    }

    setTimeout(() => {
      if (this.token && !this.socket?.connected) {
        this.connect(this.token);
      }
    }, delay);
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.isManualDisconnect = true;
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Subscribe to hold request updates
   */
  subscribeToHoldRequest(holdRequestId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('subscribe:hold_request', holdRequestId);
    }
  }

  /**
   * Unsubscribe from hold request updates
   */
  unsubscribeFromHoldRequest(holdRequestId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('unsubscribe:hold_request', holdRequestId);
    }
  }

  /**
   * Subscribe to line item updates
   */
  subscribeToLineItem(lineItemId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('subscribe:line_item', lineItemId);
    }
  }

  /**
   * Unsubscribe from line item updates
   */
  unsubscribeFromLineItem(lineItemId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('unsubscribe:line_item', lineItemId);
    }
  }

  /**
   * Mark notification as read
   */
  markNotificationAsRead(notificationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('notification:mark_read', notificationId);
    }
  }

  /**
   * Request notification history
   */
  getNotifications(limit: number = 20, offset: number = 0): void {
    if (this.socket?.connected) {
      this.socket.emit('notifications:get', { limit, offset });
    }
  }

  /**
   * Request unread count refresh
   */
  refreshUnreadCount(): void {
    if (this.socket?.connected) {
      this.socket.emit('notifications:refresh_unread_count');
    }
  }

  /**
   * Send ping to check connection health
   */
  ping(): void {
    if (this.socket?.connected) {
      this.socket.emit('ping');
    }
  }

  /**
   * Add notification listener
   */
  onNotification(callback: NotificationCallback): () => void {
    this.notificationListeners.add(callback);
    return () => this.notificationListeners.delete(callback);
  }

  /**
   * Add status update listener for specific entity
   */
  onStatusUpdate(entityKey: string, callback: StatusUpdateCallback): () => void {
    if (!this.statusUpdateListeners.has(entityKey)) {
      this.statusUpdateListeners.set(entityKey, new Set());
    }
    this.statusUpdateListeners.get(entityKey)!.add(callback);
    
    return () => {
      const listeners = this.statusUpdateListeners.get(entityKey);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.statusUpdateListeners.delete(entityKey);
        }
      }
    };
  }

  /**
   * Add unread count listener
   */
  onUnreadCount(callback: UnreadCountCallback): () => void {
    this.unreadCountListeners.add(callback);
    return () => this.unreadCountListeners.delete(callback);
  }

  /**
   * Add connection status listener
   */
  onConnectionChange(callback: ConnectionCallback): () => void {
    this.connectionListeners.add(callback);
    return () => this.connectionListeners.delete(callback);
  }

  /**
   * Notify all notification listeners
   */
  private notifyNotificationListeners(notification: NotificationPayload): void {
    this.notificationListeners.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  /**
   * Notify status update listeners for specific entity
   */
  private notifyStatusUpdateListeners(entityKey: string, update: StatusUpdatePayload): void {
    const listeners = this.statusUpdateListeners.get(entityKey);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(update);
        } catch (error) {
          console.error('Error in status update listener:', error);
        }
      });
    }
  }

  /**
   * Notify all unread count listeners
   */
  private notifyUnreadCountListeners(count: number): void {
    this.unreadCountListeners.forEach(callback => {
      try {
        callback(count);
      } catch (error) {
        console.error('Error in unread count listener:', error);
      }
    });
  }

  /**
   * Notify all connection listeners
   */
  private notifyConnectionListeners(connected: boolean): void {
    this.connectionListeners.forEach(callback => {
      try {
        callback(connected);
      } catch (error) {
        console.error('Error in connection listener:', error);
      }
    });
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get socket ID
   */
  getSocketId(): string | undefined {
    return this.socket?.id;
  }
}

// Export singleton instance
export const websocketClient = new WebSocketClient();
